import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import EngagementButtons from '../components/engagement/EngagementButtons';
import AddToListButton from '../components/engagement/AddToListButton';
import InviteFriendButton from '../components/engagement/InviteFriendButton';
import { Starfield } from '../components/Starfield';
import { useI18n, useTranslate } from '../contexts/I18nContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { SEO, BASE_URL, absoluteUrl } from '../components/SEO';
import { formatChile } from '../utils/dateUtils';
import { optimizedImageUrl, DEFAULT_EVENT_IMAGE, handleEventImageError, eventImageOrDefault } from '../utils/imageUrl';
import { isPriceUnknown, PRICE_TBC_LABEL } from '../utils/price';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Calendar, MapPin, Clock, DollarSign, Users, Star,
  Share2, Heart, ChevronLeft, ExternalLink, Phone, Mail, User, Send, Loader2, Bell, Ticket, Copy,
  Building2, Video
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_LABELS = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
};

/**
 * Calculates if a permanent event is currently open based on Chile time and operating hours.
 * Handles cross-midnight hours (e.g., 18:00-02:00).
 */
const getOpenStatus = (operatingHours, manualOverrideClosed) => {
  if (manualOverrideClosed) return { isOpen: false, reason: 'closed_manual' };
  if (!operatingHours) return { isOpen: false, reason: 'no_hours' };
  
  // Get current Chile time
  const now = new Date();
  const chileTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Santiago' }));
  const dayIndex = chileTime.getDay(); // 0=Sunday
  const currentDay = DAY_KEYS[dayIndex];
  const currentMinutes = chileTime.getHours() * 60 + chileTime.getMinutes();
  
  // Check today's hours
  const todayHours = operatingHours[currentDay];
  if (todayHours) {
    // All-day: always open
    if (todayHours.all_day) {
      return { isOpen: true, closesAt: null, allDay: true };
    }
    const [openH, openM] = todayHours.open.split(':').map(Number);
    const [closeH, closeM] = todayHours.close.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    
    if (closeMinutes > openMinutes) {
      // Same-day hours (e.g., 09:00-18:00)
      if (currentMinutes >= openMinutes && currentMinutes < closeMinutes) {
        return { isOpen: true, closesAt: todayHours.close };
      }
    } else {
      // Cross-midnight (e.g., 18:00-02:00): open from openMinutes until midnight
      if (currentMinutes >= openMinutes) {
        return { isOpen: true, closesAt: todayHours.close };
      }
    }
  }
  
  // Check if yesterday's hours extend past midnight into today
  const yesterdayIndex = (dayIndex + 6) % 7;
  const yesterdayDay = DAY_KEYS[yesterdayIndex];
  const yesterdayHours = operatingHours[yesterdayDay];
  if (yesterdayHours) {
    const [openH, openM] = yesterdayHours.open.split(':').map(Number);
    const [closeH, closeM] = yesterdayHours.close.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    
    if (closeMinutes <= openMinutes && currentMinutes < closeMinutes) {
      // We're in the after-midnight window of yesterday's hours
      return { isOpen: true, closesAt: yesterdayHours.close };
    }
  }
  
  // Find next opening time
  for (let i = 0; i < 7; i++) {
    const checkDayIndex = (dayIndex + i) % 7;
    const checkDay = DAY_KEYS[checkDayIndex];
    const hours = operatingHours[checkDay];
    if (hours) {
      if (i === 0) {
        // Today - check if opens later
        const [openH, openM] = hours.open.split(':').map(Number);
        const openMinutes = openH * 60 + openM;
        if (currentMinutes < openMinutes) {
          return { isOpen: false, opensAt: hours.open, opensDay: checkDay };
        }
      } else {
        return { isOpen: false, opensAt: hours.open, opensDay: checkDay };
      }
    }
  }
  
  return { isOpen: false, reason: 'no_hours' };
};

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const customIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:#00CC66;width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const RecenterButton = ({ lat, lng }) => {
  const map = useMap();
  return (
    <button
      onClick={() => map.setView([lat, lng], 15)}
      className="absolute top-2 right-2 z-[1000] bg-white rounded-lg shadow-md p-2 hover:bg-gray-100 transition-colors"
      title="Volver al pin"
      style={{ zIndex: 1000 }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#064E3B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
      </svg>
    </button>
  );
};

export const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { translateText } = useTranslate();
  const { formatPrice } = useCurrency();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  
  // Contact form state
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    sender_name: '',
    sender_phone: '',
    sender_email: '',
    message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // Subscription form state
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState({
    name: '',
    email: '',
    commune_id: '',
    subscription_type: 'category' // 'category' or 'provider'
  });
  const [subscriptionSubmitting, setSubscriptionSubmitting] = useState(false);
  const [communes, setCommunes] = useState([]);

  // Discount code state
  const [discountCode, setDiscountCode] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountChecked, setDiscountChecked] = useState(false);

  // Engagement state (favorite + plan)
  const [isFavorite, setIsFavorite] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);
  const [tourStops, setTourStops] = useState([]);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    fetchEvent();
    fetchCommunes();
    // Track a view for this event (fire-and-forget)
    if (id) {
      axios.post(`${API_URL}/api/events/${id}/view`).catch(() => {});
      // If logged in, also record view in the personal history for recommendations
      if (isLoggedIn) {
        import('../lib/meApi').then(m => m.trackUserView(id));
        // And fetch favorite/plan state for this specific event
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        axios.get(`${API_URL}/api/me/favorites/ids`, { headers })
          .then(r => setIsFavorite(Array.isArray(r.data) && r.data.includes(id)))
          .catch(() => {});
        axios.get(`${API_URL}/api/me/plans/ids`, { headers })
          .then(r => setPlanStatus((r.data || {})[id] || null))
          .catch(() => {});
      }
    }
  }, [id]);

  // Track an interaction (CTA click) — fire-and-forget
  const trackInteraction = () => {
    if (!id) return;
    axios.post(`${API_URL}/api/events/${id}/click`).catch(() => {});
  };

  const fetchCommunes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/territories/communes`);
      setCommunes(response.data);
    } catch (error) {
      console.error('Failed to fetch communes:', error);
    }
  };

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/events/${id}`);
      setEvent(response.data);
      // If this event is part of a tour, load the other stops (public endpoint).
      if (response.data.tour_id) {
        try {
          const t = await axios.get(`${API_URL}/api/events/tour/${response.data.tour_id}/public`);
          setTourStops(Array.isArray(t.data) ? t.data : []);
        } catch { setTourStops([]); }
      } else {
        setTourStops([]);
      }
      // Check if user already has a discount code
      if (response.data.discounts_enabled && response.data.max_discount_codes !== 0) {
        checkExistingDiscount(response.data.id);
      }
    } catch (error) {
      console.error('Failed to fetch event:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingDiscount = async (eventId) => {
    try {
      const response = await axios.get(`${API_URL}/api/discounts/${eventId}/check`);
      if (response.data.has_code) {
        setDiscountCode(response.data.code);
      }
      setDiscountChecked(true);
    } catch (error) {
      setDiscountChecked(true);
    }
  };

  const handleGenerateDiscount = async () => {
    setDiscountLoading(true);
    trackInteraction();
    try {
      const response = await axios.post(`${API_URL}/api/discounts/${event.id}/generate`);
      setDiscountCode(response.data.code);
      if (response.data.already_generated) {
        toast.info('Ya tienes un código para este evento');
      } else {
        toast.success('Código de descuento generado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar código');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copiado al portapapeles');
    trackInteraction();
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    if (!contactForm.sender_name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!contactForm.sender_phone.trim()) {
      toast.error('El teléfono es obligatorio');
      return;
    }
    if (!contactForm.sender_email.trim()) {
      toast.error('El email es obligatorio');
      return;
    }
    if (!contactForm.message.trim()) {
      toast.error('La pregunta es obligatoria');
      return;
    }
    
    setContactSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/messages/event/${id}`, contactForm);
      toast.success('¡Mensaje enviado!', {
        description: 'El organizador se pondrá en contacto contigo pronto.'
      });
      setContactDialogOpen(false);
      setContactForm({
        sender_name: '',
        sender_phone: '',
        sender_email: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el mensaje');
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    
    // Validate fields
    if (!subscriptionForm.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!subscriptionForm.email.trim()) {
      toast.error('El email es obligatorio');
      return;
    }
    
    setSubscriptionSubmitting(true);
    try {
      const payload = {
        name: subscriptionForm.name,
        email: subscriptionForm.email,
        commune_id: subscriptionForm.commune_id || null,
        subscription_type: subscriptionForm.subscription_type,
        category_id: subscriptionForm.subscription_type === 'category' ? event.category_id : null,
        provider_id: subscriptionForm.subscription_type === 'provider' ? event.provider_id : null
      };
      
      await axios.post(`${API_URL}/api/subscriptions/`, payload);
      toast.success('¡Suscripción exitosa!', {
        description: 'Te notificaremos cuando haya nuevos eventos.'
      });
      setSubscriptionDialogOpen(false);
      setSubscriptionForm({
        name: '',
        email: '',
        commune_id: '',
        subscription_type: 'category'
      });
    } catch (error) {
      console.error('Failed to subscribe:', error);
      toast.error(error.response?.data?.detail || 'Error al suscribirse');
    } finally {
      setSubscriptionSubmitting(false);
    }
  };

  const handleRate = async () => {
    if (selectedRating === 0) return;
    
    try {
      setRatingSubmitting(true);
      await axios.post(`${API_URL}/api/events/${id}/rate`, {
        score: selectedRating
      });
      setRatingDialogOpen(false);
      fetchEvent();
    } catch (error) {
      console.error('Failed to rate event:', error);
    } finally {
      setRatingSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatChile(dateStr, "EEEE d 'de' MMMM, yyyy", locale);
  };

  const audienceLabels = {
    all: t('allAudiences') !== 'allAudiences' ? t('allAudiences') : 'Todo público',
    kids: t('kids'),
    teen: t('teens') !== 'teens' ? t('teens') : 'Adolescentes',
    adult: t('adults'),
    adults: t('adults'),
    family: t('family'),
  };

  if (loading) {
    return (
      <div className="min-h-screen dark-starfield py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-96 w-full rounded-3xl mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen dark-starfield flex items-center justify-center">
        <SEO title="Evento no encontrado" path={`/event/${id}`} noIndex />
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Evento no encontrado</h2>
          <Button onClick={() => navigate('/explore')} className="btn-primary">
            Volver a explorar
          </Button>
        </div>
      </div>
    );
  }

  const eventUrl = `${BASE_URL}/event/${event.id}`;
  const eventImage = absoluteUrl(eventImageOrDefault(event.main_image));
  const eventJsonLd = {
    "@context": "https://schema.org",
    "@type": event.is_permanent ? "LocalBusiness" : "Event",
    "name": event.name,
    "url": eventUrl,
    "description": event.description || `${event.name} - ${event.is_permanent ? 'Local permanente' : 'Evento'} en ${event.commune_name || 'Chile'}`,
    ...(!event.is_permanent && event.start_date && { "startDate": event.start_date }),
    ...(!event.is_permanent && event.end_date && { "endDate": event.end_date || event.start_date }),
    ...(!event.is_permanent && { "eventStatus": "https://schema.org/EventScheduled" }),
    ...(!event.is_permanent && {
      "eventAttendanceMode": event.is_online
        ? "https://schema.org/OnlineEventAttendanceMode"
        : "https://schema.org/OfflineEventAttendanceMode"
    }),
    "image": [eventImage],
    ...(event.is_online && !event.is_permanent
      ? {
          "location": {
            "@type": "VirtualLocation",
            "url": event.online_url || eventUrl
          }
        }
      : event.is_permanent
        ? {
            "address": {
              "@type": "PostalAddress",
              ...(event.address && { "streetAddress": event.address }),
              ...(event.commune_name && { "addressLocality": event.commune_name }),
              ...(event.region_name && { "addressRegion": event.region_name }),
              "addressCountry": "CL"
            },
            ...(event.latitude && event.longitude && {
              "geo": { "@type": "GeoCoordinates", "latitude": event.latitude, "longitude": event.longitude }
            })
          }
        : {
            "location": {
              "@type": "Place",
              "name": event.venue_name || event.address || event.commune_name || 'Chile',
              "address": {
                "@type": "PostalAddress",
                ...(event.address && { "streetAddress": event.address }),
                ...(event.commune_name && { "addressLocality": event.commune_name }),
                ...(event.region_name && { "addressRegion": event.region_name }),
                "addressCountry": "CL"
              },
              ...(event.latitude && event.longitude && {
                "geo": { "@type": "GeoCoordinates", "latitude": event.latitude, "longitude": event.longitude }
              })
            }
          }),
    ...(event.provider_name && {
      "organizer": {
        "@type": "Organization",
        "name": event.provider_name,
        ...(event.provider_id && { "url": `${BASE_URL}/artist/${event.provider_id}` })
      }
    }),
    "offers": {
      "@type": "Offer",
      ...((event.is_free || (event.price !== null && event.price !== undefined)) && {
        "price": event.is_free ? 0 : event.price,
        "priceCurrency": event.currency || "CLP",
      }),
      "availability": "https://schema.org/InStock",
      "url": (!event.is_online && event.online_url) || eventUrl
    },
    ...(event.avg_rating > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": event.avg_rating,
        "ratingCount": event.ratings_count || 1,
        "bestRating": 5
      }
    })
  };

  return (
    <div className="min-h-screen dark-starfield text-white relative" data-testid="event-detail-page">
      {/* Animated stars + satellites (always on for event pages) */}
      <Starfield force />
      {/* Ambient color glows like the Landing hero */}
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-40 -left-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.55) 0%, rgba(0,204,102,0) 70%)' }}
        />
        <div
          className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(239,68,68,0.55) 0%, rgba(239,68,68,0) 70%)' }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[700px] h-[400px] rounded-full blur-3xl opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.6) 0%, rgba(139,92,246,0) 70%)' }}
        />
      </div>
      <SEO
        title={`${event.name} - ${event.commune_name || 'Evento'}`}
        description={event.description?.substring(0, 160) || `${event.name} en ${event.commune_name || 'Chile'}. ${event.category_name || 'Evento'}. Descubre panoramas y actividades cerca de ti en PlanCity.`}
        path={`/event/${event.id}`}
        type="article"
        image={eventImage}
        jsonLd={eventJsonLd}
      />
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <Button
            variant="ghost"
            className="rounded-full -ml-2"
            onClick={() => {
              // If there is no in-app history (e.g. event opened in a new tab from the
              // admin/provider dashboard), navigate(-1) lands the user on a blank tab.
              // Fall back to the home page so the back button is always useful.
              if (window.history.length > 1 && document.referrer && new URL(document.referrer).origin === window.location.origin) {
                navigate(-1);
              } else {
                navigate('/');
              }
            }}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {t('back')}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Main Image - dynamic aspect with blur backdrop so vertical/square posters show in full */}
        <div className="relative rounded-3xl overflow-hidden mb-2 shadow-xl bg-neutral-900">
          {(() => {
            const rawImg = activeImage || event.main_image || DEFAULT_EVENT_IMAGE;
            const isDefault = rawImg === DEFAULT_EVENT_IMAGE;
            const imgSrc = optimizedImageUrl(rawImg, 800);
            const imgBlur = optimizedImageUrl(rawImg, 200);
            const objectPosition = (() => {
              if (!activeImage || activeImage === event.main_image) return `50% ${event.image_position || '50%'}`;
              const found = (event.additional_images || []).find(img => (typeof img === 'string' ? img : img?.url) === activeImage);
              const pos = typeof found === 'object' ? found?.position || '50%' : '50%';
              return `50% ${pos}`;
            })();
            const handleImgError = handleEventImageError;
            return (
              <div className="relative w-full" style={{ aspectRatio: '16 / 10' }}>
                {/* Blurred backdrop fills the frame regardless of poster orientation */}
                <img
                  src={imgBlur}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60"
                  loading="eager"
                  decoding="async"
                  onError={handleImgError}
                />
                {/* Foreground image always fully visible */}
                <img
                  src={imgSrc}
                  alt={`${event.name} - ${event.category_name || 'Evento'} en ${event.commune_name || 'Chile'}`}
                  className={`absolute inset-0 w-full h-full ${isDefault ? 'object-cover' : 'object-contain'}`}
                  style={{ objectPosition }}
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  onError={handleImgError}
                />
              </div>
            );
          })()}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
            {event.is_free && (
              <Badge className="bg-[#00CC66] text-white border-0 rounded-full px-4">
                {t('freeEvent')}
              </Badge>
            )}
            {event.is_featured && (
              <Badge className="bg-white/90 text-[#064E3B] border-0 rounded-full px-4">
                {translateText('Destacado')}
              </Badge>
            )}
            {event.is_permanent && (
              <Badge className="bg-blue-500 text-white border-0 rounded-full px-4" data-testid="permanent-badge">
                <Building2 className="w-3 h-3 mr-1" />
                Permanente
              </Badge>
            )}
            {event.is_recurring && (
              <Badge className="bg-orange-500 text-white border-0 rounded-full px-4" data-testid="recurring-badge">
                <Building2 className="w-3 h-3 mr-1" />
                Recurrente
              </Badge>
            )}
            {event.is_online && (
              <Badge className="bg-violet-600 text-white border-0 rounded-full px-4" data-testid="online-badge">
                <Video className="w-3 h-3 mr-1" />
                Online
              </Badge>
            )}
            {event.is_permanent && (() => {
              const status = getOpenStatus(event.operating_hours, event.manual_override_closed);
              return status.isOpen ? (
                <Badge className="bg-green-500 text-white border-0 rounded-full px-4" data-testid="open-badge">
                  Abierto
                </Badge>
              ) : (
                <Badge className="bg-red-500 text-white border-0 rounded-full px-4" data-testid="closed-badge">
                  Cerrado
                </Badge>
              );
            })()}
            {!event.is_permanent && !event.is_recurring && event.start_date && (() => {
              const start = new Date(event.start_date);
              const now = new Date();
              if (start > now) {
                const diff = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
                const months = Math.floor(diff / 30);
                const label = diff <= 30
                  ? translateText(`Faltan ${diff} días`)
                  : months <= 1
                    ? translateText('Falta 1 mes')
                    : translateText(`Faltan ${months} meses`);
                return (
                  <Badge className="bg-orange-500 text-white border-0 rounded-full px-4" data-testid="upcoming-badge">
                    {label}
                  </Badge>
                );
              }
              return null;
            })()}
          </div>

          {/* Future event "Próximamente" badge - removed, now inside badges div */}

          {/* Share + Engagement buttons */}
          <div className="absolute top-4 right-4 flex gap-2 items-center">
            <EngagementButtons
              eventId={id}
              isFavorite={isFavorite}
              planStatus={planStatus}
              isAuthenticated={isLoggedIn}
              onLoginRequired={() => navigate(`/login?next=/event/${id}`)}
              compact={false}
            />
            <AddToListButton
              eventId={id}
              isAuthenticated={isLoggedIn}
              onLoginRequired={() => navigate(`/login?next=/event/${id}`)}
              compact={false}
            />
            <InviteFriendButton
              eventId={id}
              eventName={event?.name || ''}
              isAuthenticated={isLoggedIn}
              onLoginRequired={() => navigate(`/login?next=/event/${id}`)}
              compact={false}
            />
            <Button
              size="icon"
              variant="secondary"
              className="rounded-full bg-black/50 hover:bg-black/70 text-white"
              onClick={handleShare}
              data-testid="share-btn"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>

          {/* Price */}
          {isPriceUnknown(event) && (
            <div className="absolute bottom-4 right-4" data-testid="event-detail-price-tbc">
              <div className="bg-black/60 backdrop-blur text-white rounded-full px-4 py-2 font-semibold text-sm shadow-xl ring-1 ring-white/30">
                {translateText(PRICE_TBC_LABEL)}
              </div>
            </div>
          )}
          {event.price !== null && event.price !== undefined && (
            <div className="absolute bottom-4 right-4">
              {(event.is_free || event.price === 0) ? (
                <div className="bg-gradient-to-r from-emerald-400 to-[#00CC66] text-white rounded-full px-4 py-2 font-bold text-sm shadow-xl ring-1 ring-white/40">
                  {translateText('Gratis')}
                </div>
              ) : (
                <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-neutral-900 rounded-full px-4 py-2 font-bold text-sm shadow-xl ring-1 ring-white/40 inline-flex items-center gap-1.5">
                  {event.price_is_from && (
                    <span className="text-[10px] font-bold opacity-80 uppercase tracking-wide">{translateText('Desde')}</span>
                  )}
                  <span>{formatPrice(event.price, event.currency)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Thumbnail strip */}
        {event.additional_images && event.additional_images.length > 0 && (
          <div className="flex gap-2 mb-8">
            <button
              type="button"
              className={`rounded-xl overflow-hidden h-16 w-24 shrink-0 border-2 transition-all ${(!activeImage || activeImage === event.main_image) ? 'border-[#00CC66] ring-1 ring-[#00CC66]' : 'border-transparent opacity-60 hover:opacity-100'}`}
              onClick={() => setActiveImage(event.main_image)}
            >
              <img src={optimizedImageUrl(event.main_image, 200)} alt="Portada" className="w-full h-full object-cover" style={{ objectPosition: `50% ${event.image_position || '50%'}` }} loading="lazy" decoding="async" />
            </button>
            {event.additional_images.map((item, idx) => {
              const imgUrl = typeof item === 'string' ? item : item?.url;
              const imgPos = typeof item === 'object' ? item?.position || '50%' : '50%';
              return (
                <button
                  key={idx}
                  type="button"
                  className={`rounded-xl overflow-hidden h-16 w-24 shrink-0 border-2 transition-all ${activeImage === imgUrl ? 'border-[#00CC66] ring-1 ring-[#00CC66]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  onClick={() => setActiveImage(imgUrl)}
                >
                  <img src={optimizedImageUrl(imgUrl, 200)} alt={`Foto ${idx + 2}`} className="w-full h-full object-cover" style={{ objectPosition: `50% ${imgPos}` }} loading="lazy" decoding="async" />
                </button>
              );
            })}
          </div>
        )}
        {!(event.additional_images && event.additional_images.length > 0) && <div className="mb-8" />}

        {/* Title and Category */}
        <div className="mb-6">
          <div className="mb-4 flex flex-wrap gap-2">
            <Badge variant="outline" className="rounded-full border-white/30 text-white bg-white/10">
              {translateText(event.category_name)}
            </Badge>
            {event.category_name_2 && (
              <Badge variant="outline" className="rounded-full border-white/30 text-white bg-white/10">
                {translateText(event.category_name_2)}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#064E3B] mb-2">
            {translateText(event.name)}
          </h1>
          {event.provider_name && event.provider_id && (
            <Link 
              to={`/artist/${event.provider_id}`}
              className="inline-flex items-center gap-3 text-gray-500 hover:text-[#00CC66] transition-colors group mt-1"
              data-testid="provider-link"
            >
              {event.provider_logo_url ? (
                <img
                  src={event.provider_logo_url}
                  alt={event.provider_name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 group-hover:border-[#00CC66] transition-colors"
                  data-testid="provider-logo"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-bold text-sm" data-testid="provider-logo-fallback">
                  {event.provider_name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-xs text-gray-400">{translateText('Publicado por')}</span>
                <span className="font-semibold text-[#064E3B] group-hover:text-[#00CC66] group-hover:underline">
                  {translateText(event.provider_name)}
                </span>
              </div>
            </Link>
          )}
        </div>

        {/* Quick info cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {event.is_permanent ? (
            <>
              {/* Open/Closed Status Card */}
              {(() => {
                const status = getOpenStatus(event.operating_hours, event.manual_override_closed);
                return (
                  <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="status-card">
                    <Clock className={`w-5 h-5 mb-2 ${status.isOpen ? 'text-green-500' : 'text-red-500'}`} />
                    <p className="text-xs text-gray-500">Estado actual</p>
                    <p className={`font-bold text-sm ${status.isOpen ? 'text-green-500' : 'text-red-500'}`}>
                      {status.isOpen ? (status.allDay ? 'Abierto 24 horas' : 'Abierto ahora') : 'Cerrado'}
                    </p>
                    {status.isOpen && status.closesAt && !status.allDay && (
                      <p className="text-xs text-gray-500 mt-1">Cierra a las {status.closesAt}</p>
                    )}
                    {!status.isOpen && status.opensAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Abre {status.opensDay ? DAY_LABELS[status.opensDay] : ''} {status.opensAt}
                      </p>
                    )}
                    {!status.isOpen && status.reason === 'closed_manual' && (
                      <p className="text-xs text-red-500 mt-1">Cerrado por contingencia</p>
                    )}
                  </div>
                );
              })()}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <Building2 className="w-5 h-5 text-blue-500 mb-2" />
                <p className="text-xs text-gray-500">Tipo</p>
                <p className="font-semibold text-sm text-[#064E3B]">Evento Permanente</p>
              </div>
            </>
          ) : event.is_recurring ? (
            <>
              {/* Recurring "next session" card */}
              {(() => {
                const status = getOpenStatus(event.operating_hours, event.manual_override_closed);
                const isLive = status.isOpen;
                return (
                  <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="recurring-status-card">
                    <Clock className={`w-5 h-5 mb-2 ${isLive ? 'text-red-500' : 'text-orange-500'}`} />
                    <p className="text-xs text-gray-500">{isLive ? translateText('Ahora') : translateText('Próxima función')}</p>
                    <p className={`font-bold text-sm ${isLive ? 'text-red-500' : 'text-orange-500'}`}>
                      {isLive
                        ? (status.allDay ? translateText('En curso · todo el día') : translateText('En curso ahora'))
                        : (status.opensDay ? `${translateText(DAY_LABELS[status.opensDay])} ${status.opensAt || ''}` : translateText('Próximamente'))}
                    </p>
                    {isLive && status.closesAt && !status.allDay && (
                      <p className="text-xs text-gray-500 mt-1">Termina a las {status.closesAt}</p>
                    )}
                  </div>
                );
              })()}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <Clock className="w-5 h-5 text-orange-500 mb-2" />
                <p className="text-xs text-gray-500">Tipo</p>
                <p className="font-semibold text-sm text-[#064E3B]">Evento Recurrente</p>
                {event.recurring_end_date && (
                  <p className="text-[11px] text-orange-700 mt-1 font-medium">
                    Hasta el {formatChile(event.recurring_end_date, "d 'de' MMMM", locale)}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <Calendar className="w-5 h-5 text-[#00CC66] mb-2" />
                <p className="text-xs text-gray-500">{t('dateAndTime')}</p>
                <p className="font-semibold text-sm text-[#064E3B]">
                  {formatChile(event.start_date, "d MMM", locale)}
                  {event.end_date && event.end_date.slice(0,10) !== event.start_date.slice(0,10) && (
                    <> - {formatChile(event.end_date, "d MMM", locale)}</>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatChile(event.start_date, "HH:mm", locale)} hrs
                </p>
              </div>
            </>
          )}
          
          {event.schedule && (
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <Clock className="w-5 h-5 text-[#00CC66] mb-2" />
              <p className="text-xs text-gray-500">{t('schedule')}</p>
              <p className="font-semibold text-sm text-[#064E3B]">{event.schedule}</p>
            </div>
          )}
          
          {event.target_audience && (
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <Users className="w-5 h-5 text-[#00CC66] mb-2" />
              <p className="text-xs text-gray-500">{t('audience')}</p>
              <p className="font-semibold text-sm text-[#064E3B]">
                {audienceLabels[event.target_audience]}
              </p>
            </div>
          )}

          {event.is_online ? (
            <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 shadow-sm">
              <Video className="w-5 h-5 text-violet-600 mb-2" />
              <p className="text-xs text-gray-500">Modalidad</p>
              <p className="font-semibold text-sm text-violet-700">
                {{
                  zoom: 'Zoom',
                  meet: 'Google Meet',
                  teams: 'Microsoft Teams',
                  youtube: 'YouTube Live',
                  twitch: 'Twitch',
                  instagram: 'Instagram Live',
                  other: 'Online'
                }[event.online_platform] || 'Evento Online'}
              </p>
            </div>
          ) : event.commune_name && (
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <MapPin className="w-5 h-5 text-[#00CC66] mb-2" />
              <p className="text-xs text-gray-500">{t('location')}</p>
              <p className="font-semibold text-sm text-[#064E3B]">{event.commune_name}</p>
            </div>
          )}
        </div>

        {/* Operating Hours Schedule for Permanent or Recurring Events */}
        {(event.is_permanent || (event.is_recurring && (event.recurring_mode || 'weekly') === 'weekly')) && event.operating_hours && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8" data-testid="operating-hours-detail">
            <h2 className="text-xl font-bold text-[#064E3B] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00CC66]" />
              {event.is_recurring ? 'Días y horarios del evento' : 'Horarios de Atención'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                const hours = event.operating_hours[day];
                const now = new Date();
                const chileTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Santiago' }));
                const todayDayKey = DAY_KEYS[chileTime.getDay()];
                const isToday = day === todayDayKey;
                return (
                  <div
                    key={day}
                    className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${
                      isToday ? 'bg-[#064E3B]/5 border border-[#064E3B]/20 font-semibold' : 'bg-gray-50'
                    }`}
                  >
                    <span className={`text-sm ${isToday ? 'text-[#064E3B]' : 'text-gray-600'}`}>
                      {DAY_LABELS[day]} {isToday && '(Hoy)'}
                    </span>
                    {hours ? (
                      <span className={`text-sm font-medium ${isToday ? 'text-[#00CC66]' : 'text-gray-700'}`}>
                        {hours.all_day ? (event.is_recurring ? 'Todo el día' : 'Abierto 24 horas') : `${hours.open} - ${hours.close}`}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 italic">{event.is_recurring ? 'Sin función' : 'Cerrado'}</span>
                    )}
                  </div>
                );
              })}
            </div>
            {event.manual_override_closed && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-center">
                <p className="text-sm font-medium text-red-700">
                  Este local se encuentra temporalmente cerrado por contingencia
                </p>
              </div>
            )}
          </div>
        )}

        {/* Recurring Dates Schedule - specific dates mode */}
        {event.is_recurring && event.recurring_mode === 'dates' && Array.isArray(event.recurring_dates) && event.recurring_dates.length > 0 && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8" data-testid="recurring-dates-detail">
            <h2 className="text-xl font-bold text-[#064E3B] mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#00CC66]" />
              Fechas y horarios del evento
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[...event.recurring_dates].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((entry, idx) => {
                const now = new Date();
                const chileTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Santiago' }));
                const todayIso = chileTime.toISOString().slice(0, 10);
                const isToday = entry.date === todayIso;
                const isPast = entry.date && entry.date < todayIso;
                const dateLabel = entry.date ? formatChile(entry.date + 'T00:00:00', "EEEE d 'de' MMMM", locale) : '';
                const labelTitle = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${
                      isToday ? 'bg-[#064E3B]/5 border border-[#064E3B]/20 font-semibold' :
                      isPast ? 'bg-gray-100 opacity-60' : 'bg-gray-50'
                    }`}
                  >
                    <span className={`text-sm ${isToday ? 'text-[#064E3B]' : isPast ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                      {labelTitle} {isToday && '(Hoy)'}
                    </span>
                    <span className={`text-sm font-medium ${isToday ? 'text-[#00CC66]' : isPast ? 'text-gray-400' : 'text-gray-700'}`}>
                      {entry.all_day ? 'Todo el día' : `${entry.open} - ${entry.close}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Instagram del Organizador (con fallback al Instagram del proveedor) */}
        {(event.organizer_instagram || event.provider_instagram) && (() => {
          const ig = event.organizer_instagram || event.provider_instagram;
          // Build proper href:
          // - if it's a full URL, use it as-is
          // - otherwise prepend https://instagram.com/ stripping any leading @
          const href = ig.startsWith('http')
            ? ig
            : `https://instagram.com/${ig.replace('@', '').trim()}`;
          // Build user-friendly label:
          // - extract username from URL (handle trailing slashes and query strings)
          // - or use the @handle / raw value if no URL
          let label = ig;
          if (ig.startsWith('http')) {
            try {
              const u = new URL(ig);
              const path = u.pathname.replace(/\/+$/g, '').split('/').filter(Boolean).pop();
              label = path ? `@${path}` : u.hostname.replace('www.', '');
            } catch (e) {
              label = ig;
            }
          } else if (!ig.startsWith('@')) {
            label = `@${ig}`;
          }
          return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackInteraction}
            className="flex items-center gap-3 bg-white rounded-3xl p-5 shadow-sm mb-8 hover:shadow-md transition-shadow group"
            data-testid="provider-instagram-link"
          >
            <div className="p-2.5 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-xl">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </div>
            <div>
              <p className="text-xs text-gray-400">Instagram del Organizador</p>
              <p className="font-semibold text-[#064E3B] group-hover:text-[#00CC66] transition-colors">
                {label}
              </p>
            </div>
          </a>
          );
        })()}

        {/* Description */}
        {event.description && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8">
            <h2 className="text-xl font-bold text-[#064E3B] mb-4">{translateText('¿Qué hay?')}</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {translateText(event.description).split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                /^https?:\/\//.test(part) ? (
                  <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#00CC66] hover:underline break-all">
                    {part}
                  </a>
                ) : part
              )}
            </p>
          </div>
        )}

        {/* Discount Code Section */}
        {event.discounts_enabled && event.max_discount_codes !== 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-3xl p-6 md:p-8 shadow-sm mb-8 border border-amber-200" data-testid="discount-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Ticket className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-amber-900">{translateText('Descuento Disponible')}</h2>
                <p className="text-sm text-amber-700">{translateText('Genera tu código y preséntalo en el evento')}</p>
              </div>
            </div>

            {discountCode ? (
              <div className="bg-white rounded-2xl p-5 border-2 border-dashed border-amber-300 text-center">
                <p className="text-xs text-amber-600 mb-2 font-medium">{translateText('Tu código de descuento')}</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl md:text-3xl font-mono font-bold tracking-widest text-amber-900" data-testid="discount-code">
                    {discountCode}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="rounded-full text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                    onClick={() => {
                      navigator.clipboard.writeText(discountCode);
                      toast.success('Código copiado');
                    }}
                    data-testid="copy-discount-btn"
                  >
                    <Copy className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-xs text-amber-500 mt-3">{translateText('Muestra este código al organizador del evento')}</p>
              </div>
            ) : (
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl py-6 text-lg font-semibold"
                onClick={handleGenerateDiscount}
                disabled={discountLoading}
                data-testid="generate-discount-btn"
              >
                {discountLoading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Ticket className="w-5 h-5 mr-2" />
                )}
                {discountLoading ? translateText('Generando...') : translateText('Generar Descuento')}
              </Button>
            )}
          </div>
        )}

        {/* Ticket sale CTA — offline events with an external purchase URL */}
        {!event.is_online && event.online_url && (
          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-3xl p-6 md:p-8 shadow-lg mb-8 text-white" data-testid="ticket-sale-panel">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <span className="text-xl">🎫</span>
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider font-semibold opacity-90">Venta de entradas</p>
                <h2 className="text-xl md:text-2xl font-bold">
                  {(() => {
                    try { return `Compra tu entrada en ${new URL(event.online_url).hostname.replace(/^www\./, '')}`; }
                    catch { return 'Compra tu entrada online'; }
                  })()}
                </h2>
              </div>
            </div>
            <p className="text-white/90 text-sm md:text-base mb-5">
              Reserva tu lugar antes de que se agoten. Te llevamos directo al sitio oficial de venta.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={event.online_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackInteraction}
                className="inline-flex items-center gap-2 rounded-full bg-white text-orange-700 hover:bg-orange-50 transition-colors px-6 py-3 font-bold shadow-lg"
                data-testid="ticket-buy-btn"
              >
                Comprar entradas
                <ExternalLink className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(event.online_url); toast.success('Link copiado al portapapeles'); }}
                className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors px-5 py-3 font-medium text-white border border-white/30"
                data-testid="ticket-copy-btn"
              >
                <Copy className="w-4 h-4" />
                Copiar link
              </button>
            </div>
          </div>
        )}

        {/* Online event "Join" panel */}
        {event.is_online && event.online_url && (
          <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl p-6 md:p-8 shadow-lg mb-8 text-white" data-testid="online-join-panel">            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-wider font-semibold opacity-90">Evento Online</p>
                <h2 className="text-xl md:text-2xl font-bold">
                  {{
                    zoom: 'Se transmite por Zoom',
                    meet: 'Se transmite por Google Meet',
                    teams: 'Se transmite por Microsoft Teams',
                    youtube: 'Se transmite por YouTube Live',
                    twitch: 'Se transmite por Twitch',
                    instagram: 'Se transmite por Instagram Live',
                    other: 'Se transmite online'
                  }[event.online_platform] || 'Se transmite online'}
                </h2>
              </div>
            </div>
            <p className="text-white/85 text-sm md:text-base mb-5">
              No necesitas trasladarte. Puedes unirte desde tu celular o computador con el link directo.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={event.online_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackInteraction}
                className="inline-flex items-center gap-2 rounded-full bg-white text-violet-700 hover:bg-violet-50 transition-colors px-6 py-3 font-bold shadow-lg"
                data-testid="online-join-btn"
              >
                <Video className="w-4 h-4" />
                Unirse al evento
                <ExternalLink className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(event.online_url);
                  toast.success('Link copiado al portapapeles');
                }}
                className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors px-5 py-3 font-medium text-white border border-white/30"
                data-testid="online-copy-btn"
              >
                <Copy className="w-4 h-4" />
                Copiar link
              </button>
            </div>
          </div>
        )}

        {/* Map - only for non-online events */}
        {!event.is_online && event.latitude && event.longitude && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8">
            <h2 className="text-xl font-bold text-[#064E3B] mb-4">{t('howToGet')}</h2>
            {event.address && (
              <p className="text-gray-600 mb-4 flex items-start gap-2">
                <MapPin className="w-5 h-5 text-[#00CC66] flex-shrink-0 mt-0.5" />
                {event.address}
              </p>
            )}
            <div className="h-64 rounded-2xl overflow-hidden relative">
              <MapContainer
                center={[event.latitude, event.longitude]}
                zoom={15}
                scrollWheelZoom={false}
                className="h-full w-full"
              >
                <LayersControl position="bottomleft">
                  <LayersControl.BaseLayer checked name="Mapa">
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satélite">
                    <TileLayer
                      attribution='&copy; Esri'
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>
                <Marker position={[event.latitude, event.longitude]} icon={customIcon}>
                  <Popup>{event.name}</Popup>
                </Marker>
                <RecenterButton lat={event.latitude} lng={event.longitude} />
              </MapContainer>
            </div>
            <Button
              variant="outline"
              className="mt-4 rounded-full"
              onClick={() => { trackInteraction(); window.open(`https://www.google.com/maps?q=${event.latitude},${event.longitude}`, '_blank'); }}
              data-testid="open-google-maps-btn"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {translateText('Abrir en Google Maps')}
            </Button>
          </div>
        )}

        {/* Tour Timeline — sister stops of the same tour */}
        {event.tour_id && tourStops.length > 1 && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8" data-testid="tour-timeline">
            <h2 className="text-xl font-bold text-[#064E3B] mb-1 flex items-center gap-2">
              <span>🎪</span> Gira · {tourStops.length} paradas
            </h2>
            <p className="text-sm text-gray-500 mb-5">Este show también pasa por otras ciudades. Encuentra la parada más cercana:</p>
            <ol className="relative border-l-2 border-emerald-200 ml-2 space-y-4">
              {tourStops.map((s) => {
                const isCurrent = s.id === event.id;
                const startDate = s.start_date ? new Date(s.start_date) : null;
                const startFmt = startDate ? startDate.toLocaleString('es-CL', {
                  timeZone: 'America/Santiago', weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
                }) : '';
                const gcalDates = (() => {
                  if (!s.start_date) return '';
                  const iso = (d) => new Date(d).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                  const startI = iso(s.start_date);
                  const endI = iso(s.end_date || s.start_date);
                  return `${startI}/${endI}`;
                })();
                const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(s.name)}&dates=${gcalDates}&details=${encodeURIComponent(s.short_description || '')}&location=${encodeURIComponent(s.address || s.commune_name || '')}`;
                const mapUrl = s.latitude && s.longitude
                  ? `https://www.google.com/maps?q=${s.latitude},${s.longitude}`
                  : (s.address ? `https://www.google.com/maps/search/${encodeURIComponent(s.address)}` : null);
                return (
                  <li key={s.id} className={`ml-4 pl-4 py-3 rounded-xl transition-colors ${isCurrent ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100'}`} data-testid={`tour-stop-item-${s.id}`}>
                    <span className={`absolute -left-[9px] mt-1 w-4 h-4 rounded-full border-2 border-white ${isCurrent ? 'bg-emerald-600' : 'bg-emerald-300'}`} />
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">{startFmt}</div>
                        <div className="text-base font-bold text-[#064E3B] mt-0.5">
                          {s.commune_name}
                          {isCurrent && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full align-middle">Estás aquí</span>}
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-1.5 mt-0.5">
                          <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="truncate">{s.venue_name || s.address || 'Ubicación por confirmar'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {mapUrl && (
                          <a
                            href={mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
                            data-testid={`tour-stop-map-${s.id}`}
                          >
                            <MapPin className="w-3.5 h-3.5" /> Mapa
                          </a>
                        )}
                        {gcalDates && (
                          <a
                            href={gcalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
                            data-testid={`tour-stop-cal-${s.id}`}
                          >
                            <Calendar className="w-3.5 h-3.5" /> Agregar al calendario
                          </a>
                        )}
                        {!isCurrent && (
                          <Link
                            to={`/event/${s.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1"
                            data-testid={`tour-stop-open-${s.id}`}
                          >
                            Ver parada
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* FAQ */}
        {event.faqs && event.faqs.length > 0 && (
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8">
            <h2 className="text-xl font-bold text-[#064E3B] mb-4">{t('faq')}</h2>
            <Accordion type="single" collapsible className="w-full">
              {event.faqs.map((faq, index) => (
                <AccordionItem key={faq.id || index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {translateText(faq.question)}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {translateText(faq.answer)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Ratings */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#064E3B]">{t('ratings')}</h2>
            <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-secondary" data-testid="rate-btn">
                  {t('rateEvent')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{translateText('¿Qué te pareció este evento?')}</DialogTitle>
                </DialogHeader>
                <div className="flex justify-center gap-2 py-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setSelectedRating(star)}
                      className="p-2 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          star <= selectedRating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <Button
                  className="btn-primary w-full"
                  onClick={handleRate}
                  disabled={selectedRating === 0 || ratingSubmitting}
                >
                  {ratingSubmitting ? translateText('Enviando...') : translateText('Enviar valoración')}
                </Button>
              </DialogContent>
            </Dialog>
          </div>

          {event.average_rating ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 ${
                      star <= Math.round(event.average_rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <div>
                <span className="text-2xl font-bold text-[#064E3B]">
                  {event.average_rating.toFixed(1)}
                </span>
                <span className="text-gray-500 ml-2">
                  ({event.rating_count} {translateText('valoraciones')})
                </span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">{translateText('Sin valoraciones todavía')}</p>
          )}
        </div>

        {/* Contact */}
        <div className="bg-[#064E3B] rounded-3xl p-6 md:p-8 text-white">
          <h2 className="text-xl font-bold mb-4">{t('contactOrganizer')}</h2>
          <p className="text-green-100 mb-6">
            {translateText('¿Tienes preguntas sobre este evento? Contacta al organizador directamente.')}
          </p>
          <div className="flex flex-wrap gap-4">
            <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-[#064E3B] hover:bg-green-50 rounded-full" data-testid="contact-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  {translateText('Enviar mensaje')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{translateText('Contactar al Organizador')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <p className="text-sm text-gray-500">
                    {translateText('Completa el formulario y el organizador se pondrá en contacto contigo.')}
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sender_name">{t('name')} *</Label>
                    <Input
                      id="sender_name"
                      value={contactForm.sender_name}
                      onChange={(e) => setContactForm({ ...contactForm, sender_name: e.target.value })}
                      placeholder={translateText('Tu nombre completo')}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sender_phone">{translateText('Teléfono')} *</Label>
                    <Input
                      id="sender_phone"
                      type="tel"
                      value={contactForm.sender_phone}
                      onChange={(e) => setContactForm({ ...contactForm, sender_phone: e.target.value })}
                      placeholder="+56 9 1234 5678"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sender_email">{t('email')} *</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={contactForm.sender_email}
                      onChange={(e) => setContactForm({ ...contactForm, sender_email: e.target.value })}
                      placeholder="tu@email.com"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">{translateText('Pregunta')} *</Label>
                    <Textarea
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder={translateText('Escribe tu pregunta o mensaje...')}
                      rows={4}
                      required
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setContactDialogOpen(false)}
                    >
                      {t('cancel')}
                    </Button>
                    <Button 
                      type="submit" 
                      className="btn-primary"
                      disabled={contactSubmitting}
                    >
                      {contactSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      {translateText('Enviar mensaje')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Subscription Button and Dialog */}
            <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#00CC66] text-white hover:bg-[#00BB55] rounded-full" data-testid="subscribe-btn">
                  <Bell className="w-4 h-4 mr-2" />
                  {translateText('Recibir notificaciones')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{translateText('Suscribirse a Notificaciones')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubscriptionSubmit} className="space-y-4">
                  <p className="text-sm text-gray-500">
                    {translateText('Recibe notificaciones cuando haya nuevos eventos similares a este.')}
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sub_name">{t('name')} *</Label>
                    <Input
                      id="sub_name"
                      value={subscriptionForm.name}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, name: e.target.value })}
                      placeholder={translateText('Tu nombre')}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sub_email">{t('email')} *</Label>
                    <Input
                      id="sub_email"
                      type="email"
                      value={subscriptionForm.email}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, email: e.target.value })}
                      placeholder="tu@email.com"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sub_commune">{translateText('Comuna (opcional)')}</Label>
                    <Select
                      value={subscriptionForm.commune_id}
                      onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, commune_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectCommune')} />
                      </SelectTrigger>
                      <SelectContent>
                        {communes.map((commune) => (
                          <SelectItem key={commune.id} value={commune.id}>
                            {commune.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-3">
                    <Label>{translateText('¿Qué te gustaría recibir?')}</Label>
                    <RadioGroup
                      value={subscriptionForm.subscription_type}
                      onValueChange={(value) => setSubscriptionForm({ ...subscriptionForm, subscription_type: value })}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="category" id="sub_category" />
                        <Label htmlFor="sub_category" className="flex-1 cursor-pointer">
                          <div className="font-medium">{translateText('Eventos de esta categoría')}</div>
                          <div className="text-sm text-gray-500">
                            {translateText(`Notificarme sobre nuevos eventos de "${event?.category_name}"`)}
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                        <RadioGroupItem value="provider" id="sub_provider" />
                        <Label htmlFor="sub_provider" className="flex-1 cursor-pointer">
                          <div className="font-medium">{translateText('Eventos de este organizador')}</div>
                          <div className="text-sm text-gray-500">
                            {translateText(`Notificarme sobre nuevos eventos de "${event?.provider_name}"`)}
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button 
                      type="submit" 
                      className="btn-primary"
                      disabled={subscriptionSubmitting}
                    >
                      {subscriptionSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4 mr-2" />
                      )}
                      {translateText('Suscribirme')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
